import { countries_options } from "@/components/Form/CountryPhone/data";
import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { z } from "zod";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { useTranslation } from "react-i18next";

const filterM2IssuersSchema = z.object({
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  country_contains: z.string().optional(),
  sector_contains: z.string().optional(),
  identification_code_contains: z.string().optional(),
  identification_code_type_contains: z.string().optional(),
  issuer_group_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
});

export type FilterM2IssuersSchema = z.infer<typeof filterM2IssuersSchema>;

export function FilterM2Issuers() {
  const { t, i18n } = useTranslation(["common", "tables"]);
  const client = Clients.M2;
  const tableName = Tables.M2_ISSUERS;
  const language = i18n.language?.startsWith("en") ? "en" : "fr";

  const fields: Field[] = [
    {
      name: "name_contains",
      label: t(`${tableName}.columns.name`, {
        ns: "tables",
        defaultValue: "Name",
      }),
      type: "asyncAutocomplete",
      base_url: "m2/m2_issuers",
      column_name: "name",
    },
    {
      name: "country_contains",
      label: t(`${tableName}.columns.country_name`, {
        ns: "tables",
        defaultValue: "Country",
      }),
      type: "autocomplete",
      options: countries_options,
    },
    {
      name: "sector_contains",
      label: t(`${tableName}.columns.sector`, {
        ns: "tables",
        defaultValue: "Sector",
      }),
      type: "select",
      useTranslation: true,
      client,
      table_name: tableName,
      language,
      translate_key: "sector",
    },
    {
      name: "identification_code_contains",
      label: t(`${tableName}.columns.identification_code`, {
        ns: "tables",
        defaultValue: "Identification code",
      }),
      type: "input",
    },
    {
      name: "identification_code_type_contains",
      label: t(`${tableName}.columns.identification_code_type`, {
        ns: "tables",
        defaultValue: "Identification code type",
      }),
      type: "input",
    },
    {
      name: "issuer_group_name_contains",
      label: t(`${tableName}.columns.issuer_group_name`, {
        ns: "tables",
        defaultValue: "Issuer group",
      }),
      type: "asyncAutocomplete",
      base_url: "m2/m2_issuers",
      column_name: "name",
    },
  ];

  return (
    <FilterTable
      client={client}
      schema={filterM2IssuersSchema}
      tableName={tableName}
      fields={fields}
    />
  );
}
